import type { DbTemplateStatus } from '@domain/schemas/contract.schema';
import { DbTemplateStatusSchema, ResourceState } from '@domain/schemas/contract.schema';
import { UserRole } from '@domain/schemas/user-role';
import { getFeatureFlag } from '@root/helpers/feature-flags';

type TemplateTransitionsByStatus = Record<DbTemplateStatus, DbTemplateStatus[]>;

const STANDARD_TEMPLATE_TRANSITIONS: TemplateTransitionsByStatus = {
  ARCHIVED: [ResourceState.DRAFT],
  DRAFT: [ResourceState.ARCHIVED, ResourceState.PUBLISHED],
  PUBLISHED: [ResourceState.DRAFT, ResourceState.ARCHIVED],
  REVIEW: [],
};

const TEMPLATE_REVIEW_ADMIN_TRANSITIONS: TemplateTransitionsByStatus = {
  ARCHIVED: [ResourceState.DRAFT],
  DRAFT: [ResourceState.REVIEW, ResourceState.ARCHIVED],
  PUBLISHED: [],
  REVIEW: [ResourceState.DRAFT, ResourceState.ARCHIVED],
};

const TEMPLATE_REVIEW_SUPERADMIN_TRANSITIONS: TemplateTransitionsByStatus = {
  ARCHIVED: [ResourceState.DRAFT],
  DRAFT: [ResourceState.REVIEW, ResourceState.ARCHIVED],
  PUBLISHED: [ResourceState.DRAFT, ResourceState.ARCHIVED],
  REVIEW: [ResourceState.PUBLISHED, ResourceState.DRAFT, ResourceState.ARCHIVED],
};

const isTemplateStatus = (status?: string): status is DbTemplateStatus =>
  DbTemplateStatusSchema.options.includes(status as DbTemplateStatus);

export const getAllowedTemplateTargetStatuses = ({
  sourceStatus,
  isTemplateReviewWorkflowEnabled,
  userRole,
}: {
  sourceStatus?: string;
  isTemplateReviewWorkflowEnabled: boolean;
  userRole?: UserRole | string;
}): DbTemplateStatus[] => {
  if (!isTemplateStatus(sourceStatus)) {
    return [];
  }

  const transitions = isTemplateReviewWorkflowEnabled
    ? userRole === UserRole.Superadmin
      ? TEMPLATE_REVIEW_SUPERADMIN_TRANSITIONS
      : TEMPLATE_REVIEW_ADMIN_TRANSITIONS
    : STANDARD_TEMPLATE_TRANSITIONS;

  return transitions[sourceStatus];
};

export const canTransitionTemplateStatus = ({
  sourceStatus,
  targetStatus,
  userRole,
}: {
  sourceStatus?: string;
  targetStatus?: string;
  userRole?: UserRole | string;
}) => {
  if (!isTemplateStatus(targetStatus)) {
    return false;
  }

  const isTemplateReviewWorkflowEnabled = getFeatureFlag('TemplateReviewWorkflow');

  if (!isTemplateReviewWorkflowEnabled && targetStatus === 'REVIEW') {
    return false;
  }

  if (sourceStatus === targetStatus) {
    return true;
  }

  return getAllowedTemplateTargetStatuses({
    sourceStatus,
    isTemplateReviewWorkflowEnabled,
    userRole,
  }).includes(targetStatus);
};
